import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Info, Calculator, BookOpen, Users, ChevronRight } from 'lucide-react';

const fmtCurrency = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v);
const fmtNumber = (v) => typeof v === 'number' ? v.toLocaleString() : v;

export default function MetricDrillDown({ open, onOpenChange, data, loading }) {
  if (!data && !loading) return null;

  const isCurrency = data?.metric === 'net_savings' || data?.metric === 'estimated_dollar_savings';
  const isPercent = data?.metric === 'roi_percentage' || data?.metric === 'success_rate' || data?.metric === 'recidivism_rate';

  const formatValue = (v) => {
    if (typeof v !== 'number') return v;
    if (isCurrency) return fmtCurrency(v);
    if (isPercent) return `${v}%`;
    return fmtNumber(v);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden rounded-2xl" data-testid="metric-drilldown-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Calculator className="text-emerald-600" size={22} />
            {data?.title || 'Metric Breakdown'}
          </DialogTitle>
          <DialogDescription>How this number is calculated and who's included</DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[70vh] pr-3 -mr-3">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="w-7 h-7 border-3 border-emerald-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : data && (
            <div className="space-y-4 pb-4">
              {/* Headline value */}
              <Card className="border-0 shadow-sm rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-600 text-white">
                <CardContent className="p-5">
                  <p className="text-xs uppercase tracking-wide text-emerald-100 mb-1">Current Value</p>
                  <p className="text-4xl font-bold">{formatValue(data.value)}</p>
                  {data.estimated_dollar_savings != null && (
                    <p className="text-emerald-100 text-sm mt-2">Estimated savings: {fmtCurrency(data.estimated_dollar_savings)}</p>
                  )}
                </CardContent>
              </Card>

              {/* Formula */}
              {data.formula && (
                <Card className="border-0 shadow-sm rounded-2xl bg-slate-50">
                  <CardContent className="p-4">
                    <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide flex items-center gap-1 mb-2">
                      <Calculator size={12} /> Formula
                    </p>
                    <code className="text-sm font-mono text-slate-800 bg-white px-3 py-1.5 rounded-md inline-block">
                      {data.formula}
                    </code>
                  </CardContent>
                </Card>
              )}

              {/* Step-by-step calculation */}
              {data.calculation_steps && data.calculation_steps.length > 0 && (
                <Card className="border-0 shadow-sm rounded-2xl">
                  <CardContent className="p-4">
                    <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-3">Step-by-Step</p>
                    <div className="space-y-2">
                      {data.calculation_steps.map((s, i) => (
                        <div key={s.step} className="flex items-start gap-3 p-2.5 bg-slate-50 rounded-lg">
                          <div className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-xs font-bold shrink-0">{i + 1}</div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-800">{s.step}</p>
                            {s.formula && <p className="text-xs text-slate-500 font-mono mt-0.5">{s.formula}</p>}
                            {s.note && <p className="text-xs text-slate-500 mt-0.5">{s.note}</p>}
                          </div>
                          <div className="text-right shrink-0">
                            <p className="text-sm font-bold text-emerald-700">
                              {s.formula?.includes('×') || s.step?.toLowerCase().includes('cost') || s.step?.toLowerCase().includes('savings')
                                ? fmtCurrency(s.value)
                                : fmtNumber(s.value)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Why this number matters */}
              {data.explanation && (
                <Card className="border-0 shadow-sm rounded-2xl bg-amber-50 border-l-4 border-amber-400">
                  <CardContent className="p-4">
                    <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide flex items-center gap-1 mb-2">
                      <Info size={12} /> What this means
                    </p>
                    <p className="text-sm text-amber-900 leading-relaxed">{data.explanation}</p>
                  </CardContent>
                </Card>
              )}

              {/* National comparison if available */}
              {(data.national_comparison || data.assumption_note) && (
                <Card className="border-0 shadow-sm rounded-2xl bg-violet-50 border-l-4 border-violet-400">
                  <CardContent className="p-4">
                    <p className="text-xs font-semibold text-violet-700 uppercase tracking-wide flex items-center gap-1 mb-2">
                      <BookOpen size={12} /> Context for Funders
                    </p>
                    {data.national_comparison && <p className="text-sm text-violet-900 mb-1">{data.national_comparison}</p>}
                    {data.assumption_note && <p className="text-sm text-violet-900">{data.assumption_note}</p>}
                  </CardContent>
                </Card>
              )}

              {/* Source */}
              {data.source && (
                <p className="text-xs text-slate-500 italic flex items-center gap-1">
                  <BookOpen size={10} /> Source: {data.source}
                </p>
              )}

              {/* Participant list */}
              {data.participants && data.participants.length > 0 && (
                <Card className="border-0 shadow-sm rounded-2xl">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide flex items-center gap-1">
                        <Users size={12} /> Contributing Participants ({data.participants.length})
                      </p>
                    </div>
                    <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
                      {data.participants.map((p) => (
                        <div key={p.id || p.full_name} className="flex items-center gap-3 p-2 hover:bg-slate-50 rounded-lg transition-colors" data-testid={`drilldown-participant-${p.id || ''}`}>
                          <div className="w-7 h-7 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center text-xs font-bold shrink-0">
                            {p.full_name?.[0]?.toUpperCase() || '?'}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-800 truncate">{p.full_name}</p>
                            {p.moved_in && <p className="text-[11px] text-slate-500">Moved in {p.moved_in} {p.property && `· ${p.property}`}</p>}
                            {p.completion_date && <p className="text-[11px] text-slate-500">Completed {p.completion_date}</p>}
                            {p.completed_at && <p className="text-[11px] text-slate-500">Completed {p.completed_at}</p>}
                            {p.outcome_date && <p className="text-[11px] text-slate-500">{p.outcome_date}{p.notes ? ` · ${p.notes}` : ''}</p>}
                          </div>
                          {p.status && <Badge variant="outline" className="text-[10px] uppercase shrink-0">{p.status}</Badge>}
                          <ChevronRight size={14} className="text-slate-300 shrink-0" />
                        </div>
                      ))}
                    </div>
                    {(data.count_active != null || data.count_completed != null) && (
                      <div className="flex gap-3 mt-3 pt-3 border-t border-slate-100 text-xs">
                        {data.count_active != null && <span className="text-emerald-700"><strong>{data.count_active}</strong> active</span>}
                        {data.count_completed != null && <span className="text-violet-700"><strong>{data.count_completed}</strong> completed</span>}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
